import { FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Popover } from '../Atoms/Popover'
import { AccountsAuthInterfase } from '../../interfaces'
import {
  ChevronDownIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { v4 } from 'uuid'
import moment from 'moment'
import { NavLink } from 'react-router-dom'
import { ConfirmModalStatus } from '../Preshipment/ModalSuccess'
import { useAppSelector } from '../../store/hooks'
import { DeleteAccountAuth } from '../../services/AccountAuthServices'
import { ModalQuestion } from '../Atoms/ModalQuestion'
import ModalLoading from '../Atoms/ModalLoading'

export enum AccountsAuthField {
  NAME,
  IDENTIFICATION,
  PHONE,
  EMAIL,
  TYPESEND,
  ADDRESS,
  DATE,
}

const TableOptionsButton = (
  <div className="flex w-8 h-8 items-center justify-center">
    <EllipsisVerticalIcon className="w-5 h-5" />
  </div>
)

interface AccountAuthProps {
  accountsAuthData: AccountsAuthInterfase[]
  sortBy: { field: AccountsAuthField; asc: boolean }
  onChangeSortBy: (field: AccountsAuthField) => void
  setReload: React.Dispatch<React.SetStateAction<boolean>>
}
export const AccountAuthTable: FC<AccountAuthProps> = ({
  accountsAuthData,
  sortBy,
  onChangeSortBy,
  setReload,
}) => {
  const [selected, setSelected] = useState<AccountsAuthInterfase>()
  const [openModal, setOpenModal] = useState(false)
  const [titleModal, setTitleOpenModal] = useState('')
  let [loading, setLoading] = useState(false)
  let [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  let [loadingTitle, setLoadingTitle] = useState('Cargando...')
  let [deleteAccount, setDeleteAccount] = useState(false)
  const user = useAppSelector((state) => state.user)!

  useEffect(() => {
    const fetchUser = async () => {
      if (deleteAccount) {
        setLoadingStatus(ConfirmModalStatus.PROCESSING)
        setLoadingTitle('Cargando...')
        setLoading(true)
        let response = await DeleteAccountAuth(
          selected?.agreementAuthID,
          user.user?.login ?? 'ccw'
        )
        if (response.didError) {
          setLoadingStatus(ConfirmModalStatus.ERROR)
          setLoadingTitle('Error: ' + response?.errorMessage)
          setLoading(true)
          setDeleteAccount(false)
        } else {
          setLoadingStatus(ConfirmModalStatus.SUCCESS)
          setLoadingTitle('Eliminado Satisfactoriamente')
          const timer = setTimeout(() => {
            setDeleteAccount(false)
            clearTimeout(timer)
          }, 2000)
        }
        setReload(true)
      }
    }
    fetchUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, deleteAccount])

  return (
    <div className="relative w-full table-fix-head mt-4 overflow-auto">
      <table className="table-auto w-full text-gray-600 text-xs text-left border border-gray-300">
        <thead className="select-none font-semibold border-b border-gray-300 bg-gray-50">
          <tr>
            <th
              className="px-2 py-2"
              onClick={() => onChangeSortBy(AccountsAuthField.NAME)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Nombre</span>
                <div className="h-4 w-4">
                  {sortBy.field === AccountsAuthField.NAME && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>
            <th
              className="px-2 py-2"
              onClick={() => onChangeSortBy(AccountsAuthField.IDENTIFICATION)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Identificacion</span>
                <div className="h-4 w-4">
                  {sortBy.field === AccountsAuthField.IDENTIFICATION && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-2 py-2 truncate"
              onClick={() => onChangeSortBy(AccountsAuthField.PHONE)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Teléfono</span>
                <div className="h-4 w-4">
                  {sortBy.field === AccountsAuthField.PHONE && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>

            <th
              className="px-2 py-2"
              onClick={() => onChangeSortBy(AccountsAuthField.EMAIL)}
            >
              <div className="flex gap-2 items-center cursor-pointer">
                <span>Email</span>
                <div className="h-4 w-4">
                  {sortBy.field === AccountsAuthField.EMAIL && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>
            <th className="px-2 py-2">
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>Servicio</span>
              </div>
            </th>
            <th className="px-2 py-2">
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>Modalidad de pago</span>
              </div>
            </th>
            <th
              className="px-2 py-2"
              onClick={() => onChangeSortBy(AccountsAuthField.DATE)}
            >
              <div className="flex gap-2 items-center cursor-pointer truncate">
                <span>Fecha Creación</span>
                <div className="h-4 w-4">
                  {sortBy.field === AccountsAuthField.DATE && (
                    <ChevronDownIcon
                      className={classNames(
                        'w-4 h-4',
                        sortBy.asc && 'rotate-180'
                      )}
                    />
                  )}
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {accountsAuthData.map((accountsAuth, index) => {
            return (
              <tr
                key={v4() + index}
                className={classNames(
                  'hover:bg-gray-200',
                  index % 2 === 0 && 'bg-gray-100'
                )}
              >
                <td className="px-2 py-1 font-semibold">
                  {accountsAuth.accountFullName}
                </td>
                <td className="px-2 py-1">
                  {accountsAuth?.taxIDentificationTypeCode +
                    accountsAuth?.identificationNumber}
                </td>
                <td className="px-2 py-1">{accountsAuth.phoneNumber}</td>
                <td className="px-2 py-1">{accountsAuth.email}</td>
                <td className="px-2 py-1">{accountsAuth.serviceDescription}</td>
                <td className="px-2 py-1">{accountsAuth.paymentModeName}</td>
                <td className="px-2 py-1">
                  {moment(accountsAuth.creationDate).format('DD/MM/YYYY')}
                </td>
                <td className="pr-2">
                  <Popover
                    button={TableOptionsButton}
                    containerClassname="!-mt-1 ml-[50px]"
                  >
                    <div className="flex flex-col font-semibold cursor-pointer">
                      <div className="flex flex-1 flex-col text-sm text-gray-700 truncate hover:bg-main-500 hover:text-white">
                        <NavLink
                          to={`/accountAuth/create/${accountsAuth.agreementAuthID}`}
                        >
                          <div className="flex gap-2 py-2 px-4 w-32 items-center">
                            <PencilIcon className="w-5 h-5" />
                            <span>Editar</span>
                          </div>
                        </NavLink>
                      </div>
                    </div>

                    <div className="flex flex-col font-semibold cursor-pointer">
                      <div
                        className="flex flex-1 flex-col text-sm text-gray-700 truncate hover:bg-main-500 hover:text-white"
                        onClick={() => {
                          setOpenModal(true)
                          setSelected(accountsAuth)
                          setTitleOpenModal(
                            `¿Desea eliminar el Destinatario Favorito "${accountsAuth.accountFullName}"?`
                          )
                        }}
                      >
                        <div className="flex gap-2 py-2 px-4 w-64 items-center">
                          <TrashIcon className="w-5 h-5" />
                          <span>Eliminar Autorizado</span>
                        </div>
                      </div>
                    </div>
                  </Popover>
                </td>
              </tr>
            )
          })}

          {accountsAuthData.length === 0 && (
            <tr>
              <td colSpan={11} className="px-4 py-4 text-center">
                No hay despachos en esta tabla.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <ModalQuestion
        open={openModal}
        title={titleModal}
        setOpen={setOpenModal}
        setAccepts={setDeleteAccount}
      ></ModalQuestion>
      <ModalLoading
        open={loading}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoading}
        onPrint={() => {}}
      />
    </div>
  )
}
